/* =========================================================================
   region.js — Royale Vapour client-side region filtering (nimbus-style)
   -------------------------------------------------------------------------
   Every product card carries  data-royale-region="on" | "fe" | "any"
   (set server-side in snippets/product-card.liquid).

   This script:
     - Defaults to Ontario ("on"), persists choice in localStorage.
     - On every page (collection, search, home), HIDES product cards whose
       region is neither "any" nor the active region. No navigation.
     - Re-runs after Shopify section renders / pagination / infinite scroll.
     - Drives the header switcher (label, flag, menu open/close).

   Regions:
     on  -> Ontario (Provincial-stamp) + accessories/hardware/untagged (any)
     fe  -> Federal (Canada-stamp)      + accessories/hardware/untagged (any)
   ========================================================================= */
(function () {
  'use strict';

  var STORAGE_KEY = 'royale_region';
  var DEFAULT_REGION = 'on';

  var REGIONS = {
    on: { label: 'Ontario', flag: '🍁' },
    fe: { label: 'Federal', flag: '🇨🇦' }
  };

  /* ---------- state ---------- */
  function getRegion() {
    var r;
    try { r = localStorage.getItem(STORAGE_KEY); } catch (e) { r = null; }
    return REGIONS[r] ? r : DEFAULT_REGION;
  }

  function setRegion(region) {
    if (!REGIONS[region]) region = DEFAULT_REGION;
    try { localStorage.setItem(STORAGE_KEY, region); } catch (e) {}
    applyRegion(region);
  }

  /* ---------- card filtering ---------- */
  // Walk up from a <product-card> to the grid item wrapper we should hide,
  // so the grid collapses cleanly with no empty cell left behind.
  function gridItemFor(card) {
    var li = card.closest(
      'li.product-grid__item, li.grid__item, li.product-list__item, .product-grid__item, .grid__item'
    );
    return li || card;
  }

  function filterCards(region) {
    var cards = document.querySelectorAll('product-card[data-royale-region], [data-royale-region]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      // Only treat actual product cards (skip the switcher / unrelated nodes).
      var cardRegion = card.getAttribute('data-royale-region');
      if (cardRegion === null) continue;
      // Recommendation cards are owned by filterRecommendations() (stricter
      // rules: accessory-only / same-region-no-accessories). Skip them here.
      if (card.closest('product-recommendations')) continue;
      // The switcher itself doesn't carry data-royale-region, so this is safe.
      var show = cardRegion === 'any' || cardRegion === region;
      var item = gridItemFor(card);
      if (show) {
        item.style.removeProperty('display');
        item.removeAttribute('data-royale-hidden');
      } else {
        item.style.display = 'none';
        item.setAttribute('data-royale-hidden', 'true');
      }
    }
  }

  /* ---------- recommendation filtering ("You may also like") ----------
     Rules (pinned to the VIEWED product, NOT the active region):
       - Accessory product  -> recommend ONLY other accessories.
       - ON product         -> recommend ONLY ON (+ any) NON-accessory products.
       - FE product         -> recommend ONLY FE (+ any) NON-accessory products.
       - any/untagged prod. -> recommend any-region NON-accessory products.
     Cards carry data-royale-region (on|fe|any) and data-royale-acc (1|0).        */
  // Best-effort: figure out the region of the PRODUCT being viewed, even when
  // the recommendations container wasn't stamped server-side (e.g. a partial
  // theme deploy). Falls back to the active (switcher) region so recs are
  // NEVER left unfiltered.
  function viewedRegionFallback() {
    // 1) Stamped main product card on the PDP, if present.
    var main = document.querySelector('main [data-royale-region]') ||
               document.querySelector('[data-royale-region]');
    // Prefer an explicit on/fe stamp from a product card on the page body
    var explicit = document.querySelector('product-card[data-royale-region="on"], product-card[data-royale-region="fe"]');
    if (explicit) return explicit.getAttribute('data-royale-region');
    // 2) Otherwise use the active region the visitor selected.
    return getRegion();
  }

  function filterRecommendations() {
    // Match ALL recommendation containers, stamped or not, so a partial deploy
    // (container missing data-royale-rec-region) can't leak cross-region recs.
    var blocks = document.querySelectorAll('product-recommendations');
    for (var b = 0; b < blocks.length; b++) {
      var block = blocks[b];
      var hasStamp = block.hasAttribute('data-royale-rec-region');
      var viewedRegion = hasStamp
        ? (block.getAttribute('data-royale-rec-region') || 'any')
        : viewedRegionFallback();
      var viewedAcc = hasStamp
        ? (block.getAttribute('data-royale-rec-acc') === '1')
        : false; // unknown context -> treat as a regular product (region-pure)
      var cards = block.querySelectorAll('product-card[data-royale-region], [data-royale-region]');
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (card.getAttribute('data-royale-region') === null) continue;
        var cardRegion = card.getAttribute('data-royale-region');
        var cardAcc = card.getAttribute('data-royale-acc') === '1';
        var show;
        if (viewedAcc) {
          // Accessory viewed -> only accessories.
          show = cardAcc;
        } else {
          // Regular product -> same-region (or any), and never an accessory.
          show = !cardAcc && (cardRegion === 'any' || cardRegion === viewedRegion);
        }
        var item = gridItemFor(card);
        if (show) {
          item.style.removeProperty('display');
          item.removeAttribute('data-royale-rec-hidden');
        } else {
          item.style.display = 'none';
          item.setAttribute('data-royale-rec-hidden', 'true');
        }
      }
      // Reveal the (now-classified) recommendation cards. The CSS anti-flash
      // guard keeps them hidden until this class is present.
      block.classList.add('royale-recs-ready');
    }

    // Custom .product-upsell widget (rendered in the buy-buttons area).
    // Items are <a class="product-upsell__item"> stamped with region/acc.
    filterUpsellWidgets();
  }

  // Region-aware filtering for the custom .product-upsell widget.
  function filterUpsellWidgets() {
    var widgets = document.querySelectorAll('.product-upsell[data-royale-rec-region]');
    for (var w = 0; w < widgets.length; w++) {
      var widget = widgets[w];
      var viewedRegion = widget.getAttribute('data-royale-rec-region') || 'any';
      var viewedAcc = widget.getAttribute('data-royale-rec-acc') === '1';
      var items = widget.querySelectorAll('.product-upsell__item[data-royale-region]');
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var r = it.getAttribute('data-royale-region');
        var a = it.getAttribute('data-royale-acc') === '1';
        var show;
        if (viewedAcc) {
          show = a;
        } else {
          show = !a && (r === 'any' || r === viewedRegion);
        }
        if (show) {
          it.removeAttribute('data-royale-rec-hidden');
        } else {
          it.setAttribute('data-royale-rec-hidden', 'true');
        }
      }
    }
  }

  /* ---------- switcher UI ---------- */
  function syncSwitcherLabels(region) {
    var meta = REGIONS[region] || REGIONS[DEFAULT_REGION];
    document.querySelectorAll('[data-royale-region-label]').forEach(function (el) {
      el.textContent = meta.label;
    });
    document.querySelectorAll('[data-royale-region-flag]').forEach(function (el) {
      el.textContent = meta.flag;
    });
    // Mark active menu item.
    document.querySelectorAll('[data-royale-region-menu] [data-region]').forEach(function (el) {
      if (el.getAttribute('data-region') === region) {
        el.setAttribute('aria-current', 'true');
      } else {
        el.removeAttribute('aria-current');
      }
    });
  }

  function applyRegion(region) {
    filterCards(region);
    filterRecommendations();
    syncSwitcherLabels(region);
  }

  function closeAllMenus() {
    document.querySelectorAll('[data-royale-region-menu]').forEach(function (menu) {
      menu.hidden = true;
    });
    document.querySelectorAll('[data-royale-region-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
  }

  function initSwitcher(root) {
    var toggle = root.querySelector('[data-royale-region-toggle]');
    var menu = root.querySelector('[data-royale-region-menu]');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var willOpen = menu.hidden;
      closeAllMenus();
      menu.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    menu.querySelectorAll('[data-region]').forEach(function (item) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        var region = item.getAttribute('data-region');
        setRegion(region);          // persist + filter in place (NO navigation)
        closeAllMenus();
      });
    });
  }

  function initAllSwitchers() {
    document.querySelectorAll('[data-royale-region-switcher]').forEach(initSwitcher);

    // Close menus on outside click / Escape.
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-royale-region-switcher]')) closeAllMenus();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllMenus();
    });
  }

  /* ---------- re-filter on dynamic content ---------- */
  // Shopify themes re-render sections (filters, pagination, infinite scroll,
  // predictive search) without a full reload. Re-apply the filter whenever
  // new product cards are inserted.
  function watchForNewCards() {
    if (!('MutationObserver' in window)) return;
    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            filterCards(getRegion());
            filterRecommendations();
          });
          break;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- boot ---------- */
  function boot() {
    initAllSwitchers();
    applyRegion(getRegion());
    watchForNewCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-apply after Shopify Section Rendering events (theme editor + ajax).
  document.addEventListener('shopify:section:load', function () {
    initAllSwitchers();
    applyRegion(getRegion());
  });
})();
